import { DELIMITER, NULL } from '../../helpers/priceTable/constants';
import { Control, useWatch } from 'react-hook-form';
import { IPrice } from '../../types/menu.types';

interface IFallbackPrice extends Omit<IPrice, 'value_with_tax'> {
  value_with_tax: number | null;
}

interface IFallbackPrices {
  basePrice: IFallbackPrice;
  priceBandDefault: IFallbackPrice | null;
  externalSystemDefault: IFallbackPrice | null;
  inputValue: IFallbackPrice;
}

const parseWatchReturn = (value: string): number | null => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? null : parsedValue;
};

/***
 Returns an object of prices that are important to a specified field, including its own value, within a pricing form
 The price IDS are form specific. For more information the structure of ids, see the PriceManagement component
 Example return:
 @return {IFallbackPrices[]} Object containing base price, price band default, external system default, and input value
 * @param itemId
 * @param externalSystemId
 * @param priceBandId
 * @param control
 */
export const useFormFallbackPrices = (
  itemId: string,
  externalSystemId: string,
  priceBandId: string,
  control: Control,
): IFallbackPrices => {
  const priceBandDefaultId = `${itemId}${DELIMITER}null${DELIMITER}${priceBandId}`;
  const externalSystemDefaultId = `${itemId}${DELIMITER}${externalSystemId}${DELIMITER}null`;
  const basePriceId = `${itemId}${DELIMITER}null${DELIMITER}null`;
  const inputName = `${itemId}${DELIMITER}${externalSystemId}${DELIMITER}${priceBandId}`;
  const ids = [basePriceId, externalSystemDefaultId, priceBandDefaultId, inputName];
  const [watchedBasePrice, watchedExternalSystemDefault, watchedPriceBandDefault, watchedInputPrice] = useWatch({
    control,
    name: ids,
  });
  const inputValue: IFallbackPrice = {
    external_system_id: externalSystemId === NULL ? null : externalSystemId,
    price_band_id: priceBandId === NULL ? null : priceBandId,
    price_id: inputName,
    tax_rate: 0,
    value_with_tax: parseWatchReturn(watchedInputPrice),
  };
  const basePrice: IFallbackPrice = {
    price_id: basePriceId,
    external_system_id: null,
    price_band_id: null,
    value_with_tax: parseWatchReturn(watchedBasePrice) ?? null,
    tax_rate: 0,
  };

  if (externalSystemId !== NULL && priceBandId !== NULL) {
    const priceBandDefault: IFallbackPrice = {
      price_id: priceBandDefaultId,
      external_system_id: null,
      price_band_id: priceBandId,
      value_with_tax: parseWatchReturn(watchedPriceBandDefault) ?? null,
      tax_rate: 0,
    };
    const externalSystemDefault: IFallbackPrice = {
      price_id: externalSystemDefaultId,
      external_system_id: externalSystemId,
      price_band_id: null,
      value_with_tax: parseWatchReturn(watchedExternalSystemDefault) ?? null,
      tax_rate: 0,
    };
    return { basePrice, priceBandDefault, externalSystemDefault, inputValue: inputValue };
  }

  return { basePrice, priceBandDefault: null, externalSystemDefault: null, inputValue };
};
